import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import { motion } from 'framer-motion';
import { Users, CheckCircle, XCircle, Clock, AlertTriangle, Dna } from 'lucide-react';
import { brandDnaActions } from '../../../actions/brandDna';
import authSlice from '../../../utils/redux/slice/authSlice';
import { colors, spacing, radius, typography, shadows } from '../../../styles/theme';

const STORAGE_KEY = 'pending_brand_invitation_token';

export default function BrandInvitationScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(authSlice.selectors.isAuthenticated);
  const token = searchParams.get('token');

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneAction, setDoneAction] = useState('');

  const fetchInvitation = useCallback(async () => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }
    try {
      const res = await brandDnaActions.getBrandInvitationByToken(token);
      if (res.success && res.data) {
        setInvitation(res.data);
      } else {
        setError('Invitation not found');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Invitation not found');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvitation();
  }, [fetchInvitation]);

  // Handle stored token after returning from login
  useEffect(() => {
    if (isAuthenticated) {
      const storedToken = localStorage.getItem(STORAGE_KEY);
      if (storedToken && !token) {
        localStorage.removeItem(STORAGE_KEY);
        navigate(`/brand-invitation?token=${storedToken}`, { replace: true });
      }
    }
  }, [isAuthenticated, token, navigate]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      if (token) localStorage.setItem(STORAGE_KEY, token);
      navigate('/');
      return;
    }
    setAccepting(true);
    try {
      await brandDnaActions.acceptBrandInvitation(token);
      setDoneAction('accepted');
      setDone(true);
      message.success('Invitation accepted! You now have access to this brand.');
      setTimeout(() => navigate(`/brand-dna/${invitation?.brand_id}`), 1500);
    } catch (err) {
      message.error(err?.response?.data?.error || err?.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    setRejecting(true);
    try {
      await brandDnaActions.rejectBrandInvitation(invitation?.invitation_id);
      setDoneAction('declined');
      setDone(true);
    } catch (err) {
      message.error(err?.response?.data?.error || err?.message || 'Failed to decline invitation');
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.xl,
      background: colors.background.primary,
    }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          width: '100%',
          maxWidth: 440,
          padding: spacing.xxl,
          borderRadius: radius.xl,
          background: colors.background.secondary,
          border: `1px solid ${colors.border.secondary}`,
          boxShadow: shadows.xl,
          textAlign: 'center',
        }}
      >
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} onGoHome={() => navigate('/')} />
        ) : done ? (
          <DoneState action={doneAction} onGoToBrands={() => navigate('/brand-dna')} />
        ) : invitation?.status !== 'pending' ? (
          <StaleState status={invitation?.status} onGoHome={() => navigate('/')} />
        ) : (
          <ActiveInvitation
            invitation={invitation}
            isAuthenticated={isAuthenticated}
            accepting={accepting}
            rejecting={rejecting}
            onAccept={handleAccept}
            onReject={handleReject}
          />
        )}
      </motion.div>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ padding: `${spacing.xl}px 0` }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: colors.surface.primary, margin: '0 auto 20px',
        animation: 'pulse 1.5s ease-in-out infinite',
      }} />
      <div style={{ height: 20, background: colors.surface.primary, borderRadius: 4, marginBottom: 12, animation: 'pulse 1.5s ease-in-out infinite' }} />
      <div style={{ height: 14, width: '60%', background: colors.surface.primary, borderRadius: 4, margin: '0 auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
      <style>{`@keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.5} }`}</style>
    </div>
  );
}

function ErrorState({ error, onGoHome }) {
  return (
    <>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(245,87,108,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
        <AlertTriangle size={26} color="#f5576c" />
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: colors.text.primary, margin: '0 0 8px' }}>Invalid Invitation</h2>
      <p style={{ color: colors.text.tertiary, fontSize: 14, margin: '0 0 28px', lineHeight: 1.6 }}>{error}</p>
      <ActionButton onClick={onGoHome} variant="secondary">Go to Home</ActionButton>
    </>
  );
}

function StaleState({ status, onGoHome }) {
  const label = status === 'accepted' ? 'already accepted' : status === 'rejected' ? 'already declined' : status === 'cancelled' ? 'cancelled by the sender' : 'expired';
  const Icon = status === 'accepted' ? CheckCircle : status === 'rejected' ? XCircle : Clock;
  const iconColor = status === 'accepted' ? '#4ade80' : '#fa8c16';

  return (
    <>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${iconColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
        <Icon size={26} color={iconColor} />
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: colors.text.primary, margin: '0 0 8px' }}>Invitation {label}</h2>
      <p style={{ color: colors.text.tertiary, fontSize: 14, margin: '0 0 28px', lineHeight: 1.6 }}>
        This invitation link is no longer valid.
      </p>
      <ActionButton onClick={onGoHome} variant="secondary">Go to Home</ActionButton>
    </>
  );
}

function DoneState({ action, onGoToBrands }) {
  const accepted = action === 'accepted';
  return (
    <>
      <motion.div
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 18 }}
        style={{ width: 64, height: 64, borderRadius: '50%', background: accepted ? 'rgba(74,222,128,0.12)' : colors.surface.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}
      >
        {accepted ? <CheckCircle size={30} color="#4ade80" /> : <XCircle size={30} color={colors.text.disabled} />}
      </motion.div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: colors.text.primary, margin: '0 0 8px' }}>
        {accepted ? 'You\'re in!' : 'Invitation declined'}
      </h2>
      <p style={{ color: colors.text.tertiary, fontSize: 14, margin: '0 0 28px', lineHeight: 1.6 }}>
        {accepted ? 'Redirecting you to the brand…' : 'You have declined this collaboration invitation.'}
      </p>
      {!accepted && <ActionButton onClick={onGoToBrands} variant="secondary">Go to Brands</ActionButton>}
    </>
  );
}

function ActiveInvitation({ invitation, isAuthenticated, accepting, rejecting, onAccept, onReject }) {
  return (
    <>
      <div style={{ width: 60, height: 60, borderRadius: radius.lg, background: 'rgba(102,126,234,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
        <Dna size={28} color="#667eea" />
      </div>
      <div style={{ marginBottom: 24 }}>
        <p style={{ color: colors.text.tertiary, fontSize: 13, margin: '0 0 6px' }}>
          <strong style={{ color: colors.text.secondary }}>{invitation.inviter_name}</strong> has invited you to collaborate on
        </p>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: colors.text.primary, margin: '0 0 4px', letterSpacing: '-0.02em' }}>
          {invitation.brand_name}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: colors.text.disabled, fontSize: 12, marginTop: 8 }}>
          <Users size={12} />
          Brand collaboration
        </div>
      </div>

      {!isAuthenticated && (
        <div style={{ padding: '10px 16px', borderRadius: radius.md, background: 'rgba(250,140,22,0.08)', border: '1px solid rgba(250,140,22,0.2)', marginBottom: 20 }}>
          <p style={{ color: '#fa8c16', fontSize: 13, margin: 0 }}>
            You need to log in to your FluxUro account to accept this invitation.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <ActionButton onClick={onReject} variant="secondary" disabled={rejecting} loading={rejecting} flex={1}>
          {rejecting ? 'Declining…' : 'Decline'}
        </ActionButton>
        <ActionButton onClick={onAccept} variant="primary" disabled={accepting} loading={accepting} flex={1}>
          {!isAuthenticated ? 'Log in to Accept' : accepting ? 'Accepting…' : 'Accept Invitation'}
        </ActionButton>
      </div>
    </>
  );
}

function ActionButton({ onClick, variant = 'primary', disabled, loading, children, flex }) {
  const isPrimary = variant === 'primary';
  return (
    <motion.button
      whileHover={!disabled ? { opacity: 0.9 } : undefined}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      onClick={onClick}
      disabled={disabled}
      style={{
        flex,
        padding: `${spacing.sm}px ${spacing.lg}px`,
        borderRadius: radius.md,
        border: isPrimary ? 'none' : `1px solid ${colors.border.primary}`,
        background: isPrimary ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent',
        color: isPrimary ? '#fff' : colors.text.secondary,
        fontSize: typography.fontSize.md,
        fontWeight: typography.fontWeight.semibold,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        fontFamily: 'inherit',
      }}
    >
      {children}
    </motion.button>
  );
}
